import { update } from "./firebaseClient";
import { scribbleLobbyRef, getScribbleLobby } from "./scribbleLobby";
import { playersToList } from "./lobbyAvatars";
import { normalizeGuess } from "./guessMatch";
import { clampText, MAX_WORD_CHARS, MAX_TABOO_CHARS } from "./tabooCardLimits";

const ACTIVE_PHASES = new Set(["drawing", "guessing"]);

export function isActiveGuessPhase(turnPhase) {
  return ACTIVE_PHASES.has(String(turnPhase || ""));
}

/** Start Taboo lobby — host picks next player before each round. */
export async function startTabooLobbyGame(lobbyName, { roundCount, playerIds, timerSeconds }) {
  const r = scribbleLobbyRef(lobbyName);
  if (!r) throw new Error("Firebase is not configured");
  const ids = (Array.isArray(playerIds) ? playerIds : []).map(String).filter(Boolean);
  const sec = Math.max(30, Number(timerSeconds) || 60);
  const rounds = Math.max(1, Number(roundCount) || 1);
  const totalGameTime = sec;

  await update(r, {
    phase: "select_player",
    gameMode: "taboo",
    roundCount: rounds,
    drawers: ids,
    game: {
      turnIndex: 0,
      turnPhase: "waiting_player",
      currentDrawerId: "",
      timerSeconds: sec,
      timerEndsAt: null,
      overallTimerTotal: totalGameTime,
      overallTimerRemaining: totalGameTime,
      overallTimerPaused: true,
      selectedWord: "",
      selectedHint: "",
      tabooOne: "",
      tabooTwo: "",
      tabooThree: "",
      guessedBy: null,
      guessedByName: "",
      guessedPoints: 0,
      correctGuessers: {},
        lastRoundCorrectGuessers: {},
    },
    canvas: { strokes: {} },
    chat: {},
    updatedAt: Date.now(),
  });
}

export function correctGuessersFromGame(game = {}) {
  const raw = game?.correctGuessers;
  if (!raw || typeof raw !== "object") return {};
  return raw;
}

/** Players who may guess (not host, not word holder). */
export function getTabooEligibleGuesserIds(lobby) {
  const hostId = String(lobby?.hostUserId || "");
  const selected = String(lobby?.game?.currentDrawerId || "");
  return playersToList(lobby?.players)
    .filter((p) => {
      const id = String(p.id);
      if (p.isHost) return false;
      if (hostId && id === hostId) return false;
      if (selected && id === selected) return false;
      return true;
    })
    .map((p) => String(p.id));
}

export function allTabooGuessersCorrect(lobby) {
  const eligible = getTabooEligibleGuesserIds(lobby);
  if (!eligible.length) return false;
  const correct = correctGuessersFromGame(lobby?.game || {});
  return eligible.every((id) => correct[id] != null);
}

/** Host confirmed next player — begin guessing round with card content. */
export async function startTabooRound(
  lobbyName,
  { playerId, word, tabooWords, timerSeconds, turnIndex }
) {
  const r = scribbleLobbyRef(lobbyName);
  if (!r) throw new Error("Firebase is not configured");
  const lobby = await getScribbleLobby(lobbyName);
  const game = lobby?.game || {};
  
  const taboo = (Array.isArray(tabooWords) ? tabooWords : [])
    .map((t) => clampText(String(t || "").trim(), MAX_TABOO_CHARS))
    .slice(0, 3);
  const sec = Math.max(30, Number(timerSeconds) || 60);
  const idx = Math.max(0, Number(turnIndex) || 0);
  const safeWord = clampText(String(word || "").trim(), MAX_WORD_CHARS);
  const shownKey = wordShownKey(safeWord);
  const shownWords = shownKey ? { [shownKey]: true } : null;

  const overallRemaining = Math.max(0, Number(game.overallTimerRemaining) || sec);
  const overallEndsAt = Date.now() + overallRemaining * 1000;

  await update(r, {
    phase: "playing",
    "game/turnIndex": idx,
    "game/turnPhase": "guessing",
    "game/currentDrawerId": String(playerId || ""),
    "game/selectedWord": safeWord,
    "game/selectedHint": taboo.join(" · "),
    "game/tabooOne": taboo[0] || "",
    "game/tabooTwo": taboo[1] || "",
    "game/tabooThree": taboo[2] || "",
    "game/timerEndsAt": overallEndsAt,
    "game/overallTimerPaused": false,
    "game/guessedBy": null,
    "game/guessedByName": "",
    "game/guessedPoints": 0,
    "game/correctGuessers": {},
    "game/lastRoundCorrectGuessers": {},
    "game/wordReleased": true,
    "game/releasedTaboo": {},
    "game/flipIndex": 0,
    "game/shownWords": shownWords,
    chat: {},
    updatedAt: Date.now(),
  });
}

function questionRowToCard(row) {
  let taboo = [row?.option_one, row?.option_two, row?.option_three]
    .map((t) => clampText(String(t || "").trim(), MAX_TABOO_CHARS))
    .filter(Boolean);
  const word = clampText(String(row?.question_name || row?.word || "").trim(), MAX_WORD_CHARS);
  if (!word) return null;
  if (taboo.length < 3) {
    const legacy = String(row?.hint || row?.correct_answer || "").trim();
    const parts = legacy.split(/[,·|]/).map((s) => s.trim()).filter(Boolean);
    if (parts.length >= 3) taboo = parts.slice(0, 3);
  }
  return taboo.length >= 3 ? { word, taboo: taboo.slice(0, 3) } : null;
}

/** Firebase-safe key for a word already shown this round. */
export function wordShownKey(word) {
  const n = normalizeGuess(word);
  if (!n) return null;
  return n.replace(/[.#$[\]/]/g, "_");
}

/** Normalized words already shown during the active round (including current card). */
export function shownWordsFromGame(game = {}) {
  const keys = new Set();
  const raw = game?.shownWords;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    Object.entries(raw).forEach(([k, v]) => {
      if (v) keys.add(k);
    });
  }
  const cur = wordShownKey(game?.selectedWord);
  if (cur) keys.add(cur);
  return keys;
}

function excludedKeysFromInput(excludedWords) {
  const keys = new Set();
  if (excludedWords instanceof Set) {
    excludedWords.forEach((k) => k && keys.add(String(k)));
    return keys;
  }
  const list = Array.isArray(excludedWords) ? excludedWords : [excludedWords];
  list.forEach((w) => {
    const k = wordShownKey(w);
    if (k) keys.add(k);
  });
  return keys;
}

/** Pick a card not yet shown this round (random among remaining). */
export function pickAlternateTabooCard(questions, excludedWords = []) {
  const excluded = excludedKeysFromInput(excludedWords);
  const candidates = (Array.isArray(questions) ? questions : [])
    .map(questionRowToCard)
    .filter((c) => {
      if (!c) return false;
      const k = wordShownKey(c.word);
      return k && !excluded.has(k);
    });
  if (!candidates.length) return null;
  return candidates[Math.floor(Math.random() * candidates.length)];
}

/** Host flips to a different question mid-round (same player, timer continues). */
export async function flipTabooCard(lobbyName, card) {
  const r = scribbleLobbyRef(lobbyName);
  if (!r) throw new Error("Firebase is not configured");
  const lobby = await getScribbleLobby(lobbyName);
  const game = lobby?.game || {};
  const tp = String(game.turnPhase || "");
  const correct = correctGuessersFromGame(game);
  const eligible = getTabooEligibleGuesserIds(lobby);
  const roundOpen =
    isActiveGuessPhase(tp) && !eligible.every((id) => correct[id] != null);
  if (!roundOpen) {
    return { ok: false, reason: "not_active" };
  }
  const taboo = (Array.isArray(card?.taboo) ? card.taboo : [])
    .map((t) => clampText(String(t || "").trim(), MAX_TABOO_CHARS))
    .slice(0, 3);
  const safeWord = clampText(String(card?.word || "").trim(), MAX_WORD_CHARS);
  if (!safeWord || taboo.length < 3) {
    return { ok: false, reason: "invalid_card" };
  }
  const shown = { ...(game.shownWords && typeof game.shownWords === "object" ? game.shownWords : {}) };
  const curKey = wordShownKey(game.selectedWord);
  if (curKey) shown[curKey] = true;
  const nextKey = wordShownKey(safeWord);
  if (!nextKey || shown[nextKey]) {
    return { ok: false, reason: "already_shown" };
  }
  shown[nextKey] = true;
  const flipIndex = (Number(game.flipIndex) || 0) + 1;
  
  const timerEndsAt = game.timerEndsAt;
  
  await update(r, {
    "game/selectedWord": safeWord,
    "game/selectedHint": taboo.join(" · "),
    "game/tabooOne": taboo[0] || "",
    "game/tabooTwo": taboo[1] || "",
    "game/tabooThree": taboo[2] || "",
    "game/releasedTaboo": {},
    "game/guessedBy": null,
    "game/guessedByName": "",
    "game/guessedPoints": 0,
    "game/correctGuessers": {},
    "game/lastRoundCorrectGuessers": {},
    "game/wordReleased": true,
    "game/flipIndex": flipIndex,
    "game/shownWords": shown,
    "game/timerEndsAt": timerEndsAt,
    chat: {},
    updatedAt: Date.now(),
  });
  return { ok: true };
}

/** Host opens the round for player guesses (answer stays host-only). */
export async function releaseWordToPlayers(lobbyName) {
  const r = scribbleLobbyRef(lobbyName);
  if (!r) throw new Error("Firebase is not configured");
  await update(r, {
    "game/wordReleased": true,
    updatedAt: Date.now(),
  });
}

/** Host taps a taboo word to show it on all player screens. */
export async function releaseTabooWord(lobbyName, index) {
  const r = scribbleLobbyRef(lobbyName);
  if (!r) throw new Error("Firebase is not configured");
  const i = Math.max(0, Math.min(2, Number(index) || 0));
  await update(r, {
    [`game/releasedTaboo/${i}`]: true,
    updatedAt: Date.now(),
  });
}

export function releasedTabooIndices(game = {}) {
  const raw = game.releasedTaboo;
  if (!raw || typeof raw !== "object") return [];
  return [0, 1, 2].filter((i) => raw[i] === true || raw[String(i)] === true);
}

export function isWordReleasedToPlayers(game = {}) {
  return game.wordReleased === true || game.wordReleased === "true";
}

export function tabooWordsFromGame(game = {}) {
  const fromFields = [game.tabooOne, game.tabooTwo, game.tabooThree]
    .map((t) => String(t || "").trim())
    .filter(Boolean);
  if (fromFields.length >= 3) return fromFields.slice(0, 3);
  const hint = String(game.selectedHint || "").trim();
  const parts = hint.split(/[,·|]/).map((s) => s.trim()).filter(Boolean);
  if (parts.length >= 3) return parts.slice(0, 3);
  return fromFields;
}

/** After round ends, host goes to player picker for next card. */
export async function openTabooPlayerSelect(lobbyName, nextTurnIndex) {
  const r = scribbleLobbyRef(lobbyName);
  if (!r) throw new Error("Firebase is not configured");
  const lobby = await getScribbleLobby(lobbyName);
  const game = lobby?.game || {};
  const prevCorrect = correctGuessersFromGame(game);
  
  const timerEndsAt = game.timerEndsAt != null ? Number(game.timerEndsAt) : null;
  const remainingSeconds = timerEndsAt ? Math.max(0, Math.ceil((timerEndsAt - Date.now()) / 1000)) : 0;
  
  await update(r, {
    phase: "select_player",
    "game/turnIndex": Math.max(0, Number(nextTurnIndex) || 0),
    "game/turnPhase": "waiting_player",
    "game/currentDrawerId": "",
    "game/timerEndsAt": null,
    "game/overallTimerRemaining": remainingSeconds,
    "game/overallTimerPaused": true,
    "game/selectedWord": "",
    "game/selectedHint": "",
    "game/tabooOne": "",
    "game/tabooTwo": "",
    "game/tabooThree": "",
    "game/wordReleased": false,
    "game/releasedTaboo": null,
    "game/flipIndex": 0,
    "game/shownWords": null,
    "game/guessedBy": null,
    "game/guessedByName": "",
    "game/guessedPoints": 0,
    "game/correctGuessers": {},
    "game/lastRoundCorrectGuessers": prevCorrect,
    chat: {},
    updatedAt: Date.now(),
  });
}

export async function advanceTabooTurn(lobbyName, opts = {}) {
  const fromRoundEnd = opts.fromRoundEnd === true;
  const r = scribbleLobbyRef(lobbyName);
  if (!r) throw new Error("Firebase is not configured");
  const lobby = await getScribbleLobby(lobbyName);
  const game = lobby?.game || {};
  const tp = String(game.turnPhase || "");

  if (fromRoundEnd && tp !== "round_end") return;
  if (!fromRoundEnd && !isActiveGuessPhase(tp)) return;

  const currentIndex = Number(game.turnIndex) || 0;
  const nextIndex = currentIndex + 1;
  const roundLimit = Math.max(1, Number(lobby?.roundCount) || 1);

  if (nextIndex >= roundLimit) {
    await update(r, {
      phase: "completed",
      "game/turnPhase": "ended",
      "game/timerEndsAt": null,
      updatedAt: Date.now(),
    });
    return;
  }

  await openTabooPlayerSelect(lobbyName, nextIndex);
}
